/**
 * The strip that explains why parts of the country are empty.
 *
 * A map drawn from a half-finished geocoding pass looks like a map of where
 * companies are NOT, and there is nothing on screen to say otherwise. So the
 * count is stated plainly, above the map rather than tucked into a settings
 * page: the operator who notices the hole is the one who needs the sentence.
 *
 * It disappears once everything with an address has been looked at, because at
 * that point it would only be reporting a number that never changes again.
 */
import { useEffect, useRef, useState } from "react";
import { MapPin, Play, Square } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import {
  getGeocodeProgress,
  startGeocode,
  stopGeocode,
  type GeocodeProgress,
} from "../../../../lib/companyMap";
import { Btn, fmtNum } from "../../../ui";

/** While a pass is running: fast enough that the meter moves rather than jumps. */
const POLL_MS = 4_000;

/**
 * While nothing is running here.
 *
 * Not zero, and that is the fix rather than an optimisation. The pass is a
 * process-wide job: it can be started from another tab, by another operator, or
 * from the background card in the sidebar. A bar that only polled after seeing
 * `running` on its very first request would sit at a frozen count for the whole
 * pass, and the operator watching it would conclude the job had died.
 *
 * Twenty seconds because the alternative cost is one request per open map per
 * twenty seconds, against a job that takes hours.
 */
const IDLE_POLL_MS = 20_000;

export function GeocodeBar({ canManage, onPlaced }: { canManage: boolean; onPlaced: () => void }) {
  const t = useT();
  const [p, setP] = useState<GeocodeProgress | null>(null);
  const [busy, setBusy] = useState(false);
  const wasRunning = useRef(false);

  useEffect(() => {
    let stopped = false;
    let timer: number | undefined;

    const tick = async () => {
      try {
        const next = await getGeocodeProgress();
        if (stopped) return;
        setP(next);
        // Redraw the map when a pass ends: the companies it placed are not on
        // screen yet, and asking the operator to reload would be an odd way to
        // finish a job they watched run.
        if (wasRunning.current && !next.running) onPlaced();
        wasRunning.current = next.running;
        timer = window.setTimeout(tick, next.running ? POLL_MS : IDLE_POLL_MS);
      } catch {
        // A failed poll is not worth a message, but it must not end the loop
        // either: one blip would otherwise leave the bar frozen for the rest of
        // the visit, which looks exactly like a job that stopped.
        if (!stopped) timer = window.setTimeout(tick, IDLE_POLL_MS);
      }
    };

    void tick();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [onPlaced]);

  if (!p) return null;
  const looked = p.placed + p.missed;
  if (p.pending === 0 && !p.running) return null;

  const pct = p.withAddress ? Math.round((looked / p.withAddress) * 100) : 0;

  async function toggle() {
    setBusy(true);
    try {
      if (p?.running) await stopGeocode();
      else await startGeocode();
      setP(await getGeocodeProgress());
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="crm-card mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
      <MapPin className="size-4 shrink-0" style={{ color: "var(--color-primary)" }} />

      <div className="min-w-[14rem] flex-1">
        <div className="text-[12.75px] leading-snug">
          {p.running
            ? t("ariregister.map.geoRunning", { done: fmtNum(looked), pending: fmtNum(p.pending) })
            : t("ariregister.map.geoPending", { pending: fmtNum(p.pending) })}
        </div>
        {/* ⚠ The misses have to be named, and this line is why.
            `placed + missed + pending` is the whole of `withAddress`, so a
            sentence that reports only `placed` and `pending` drops a category
            and stops adding up - it read 123 483 + 226 341 against a total of
            350 543, and the background card beside it (which counts everything
            looked at) showed a different number for the same pass. The progress
            bar was always measuring `placed + missed`; only the words were
            short. A miss is a decision ABOUT an address, not a failure of the
            run: it is never retried, so it belongs in "done" - but silently, it
            would look like an address still on its way. */}
        {p.missed > 0 ? (
          <div className="crm-mut mt-0.5 text-[11.5px]">
            {t("ariregister.map.geoMissed", { missed: fmtNum(p.missed) })}
          </div>
        ) : null}
        {/* A bar rather than a spinner: this pass takes hours, and a spinner
            would say "wait" about something nobody should wait for. */}
        <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-[var(--crm-hair)]">
          <div
            className="h-full rounded-full transition-[width] duration-500"
            style={{ width: `${pct}%`, background: "var(--color-primary)" }}
          />
        </div>
      </div>

      <span className="crm-tnum crm-mut shrink-0 text-[12px]">{pct}%</span>

      {canManage ? (
        <Btn small tone={p.running ? "plain" : "dark"} onClick={toggle} disabled={busy}>
          {p.running ? <Square className="size-3.5" /> : <Play className="size-3.5" />}
          {p.running ? t("ariregister.map.geoStop") : t("ariregister.map.geoStart")}
        </Btn>
      ) : null}
    </div>
  );
}
